import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Fade, Button } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached';
import { AppDispatch, RootState } from '../../../../Redux/store';
import PopupDialog from './Common/popupDialog';
import { SearchBar } from '../../../../styles/textField';
import { buttonIconStyles } from '../../../../styles/icon';
import { deleteUserDetails } from '../../../../Redux/Slices/Application/DeviceManagement/deviceManagementHandler';
import { getApplicationStatus } from '../../../../Redux/Slices/Application/applicationHandler';
import { updateEditPageName } from '../../../../Redux/Slices/Application/appSlice';
import ActionsTable from '../../../../components/Table/actionsTable';
import {
  formatDate,
  handleSearch,
} from '../../../../utils/Groups/groupUtility';

interface DeviceDetails {
  os: string | null;
  ip: string | null;
  browser: string | null;
}

interface UserDetail {
  username: string;
  mac_address: string;
  approval_request_time: string;
  device_validity: string;
  permanent_access: boolean;
  status: string;
  access: boolean;
  device_details: DeviceDetails;
}
function DeviceManagement() {
  const { editScreen, app } = useSelector(
    (state: RootState) => state.application,
  );
  const { commonID, tenantID } = useSelector(
    (state: RootState) => state.basicSettings,
  );
  const dispatch = useDispatch<AppDispatch>();

  const { userDetails } = useSelector(
    (state: RootState) => state.basicSettings.deviceManagement,
  );
  const [
    selectedRowForPopover,
    setSelectedRowForPopover,
  ] = useState<UserDetail | null>(null);

  const [open, setOpen] = useState(false);
  const handleDevice = (row: any) => {
    setSelectedRowForPopover(row);
    setOpen(true);
  };

  const deleteDevice = async (row: any) => {
    await dispatch(
      deleteUserDetails({
        commonID,
        tenantID,
        app,
        username: row?.username,
        macAddress: row?.mac_address,
      }),
    );
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRowForPopover(null);
  };

  const flattenedUserDetails = userDetails?.map((user) => ({
    username: user.username,
    ip: user.device_details.ip,
    os: user.device_details.os,
    mac_address: user.mac_address,
    browser: user.device_details.browser,
    approval_request_time: formatDate(user.approval_request_time),
    status: user.status,
  }));
  const [filteredUsers, setFilteredUsers] = useState(flattenedUserDetails);
  useEffect(() => {
    setFilteredUsers(flattenedUserDetails);
  }, [userDetails]);

  const styles = {
    height: { xxl: '30.938rem', xl: '18.75rem', md: '18.75rem' },
  };

  return (
    <Fade
      in={editScreen === 'DeviceManagement'}
      {...(editScreen === 'DeviceManagement' ? { timeout: 1000 } : {})}
    >
      <Box>
        <Box
          sx={{
            display: 'flex',
            gap: 4,
            p: '1rem',
            pt: '1.5rem',
          }}
        >
          <SearchBar
            placeholder="Search for device details"
            onChange={(event: any) => handleSearch(event, flattenedUserDetails, setFilteredUsers)}
            sx={{
              maxWidth: {
                xxl: '25rem',
                xl: '20rem',
              },
            }}
          />
          <Button
            size="small"
            startIcon={<CachedIcon sx={buttonIconStyles} />}
            onClick={() => {
              dispatch(getApplicationStatus());
              dispatch(updateEditPageName('DeviceManagement'));
            }}
            sx={{ marginBottom: 1 }}
          >
            Reload
          </Button>
        </Box>
        <Box>
          <ActionsTable
            data={filteredUsers}
            columns={[
              { field: 'username', headerName: 'Username ' },
              { field: 'mac_address', headerName: 'Mac Address' },
              { field: 'ip', headerName: 'IP address' },
              {
                field: 'approval_request_time',
                headerName: 'Approval Request time',
              },
              { field: 'os', headerName: 'Operating System' },
              { field: 'status', headerName: 'Status' },
            ]}
            styles={styles}
            onEdit={handleDevice}
            onDelete={deleteDevice}
            pages="Device Management"
          />
        </Box>
        <PopupDialog
          open={open}
          onClose={handleClose}
          data={selectedRowForPopover}
          commonID={commonID}
          tenantID={tenantID}
          app={app}
        />
      </Box>
    </Fade>
  );
}

export default DeviceManagement;
